import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '2.5rem' }}>
    <h2 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.15rem', letterSpacing: '1px', marginBottom: '0.9rem', color: 'var(--text)' }}>{title}</h2>
    <div style={{ color: 'var(--muted)', lineHeight: '1.85', fontSize: '0.9rem' }}>{children}</div>
  </div>
);

export default function Privacy() {
  return (
    <div className="page-shell page-stack" style={{ maxWidth: '820px', margin: '0 auto' }}>
      <section className="section-stack">
        <div className="section-tag">Legal</div>
        <h1 className="section-title">Privacy Policy</h1>
        <p className="section-copy">
          Last updated: March 2026. This policy explains how CarbonX collects, uses, and protects your personal information.
        </p>
      </section>

      <div className="surface-panel" style={{ padding: '2.5rem' }}>
        <Section title="1. Information We Collect">
          <p>We collect the following categories of information:</p>
          <p style={{ marginTop: '0.8rem' }}><strong>Account Information:</strong> Name, email address, mobile number, PAN number, GSTIN (optional), and registered address — collected during signup and KYC.</p>
          <p style={{ marginTop: '0.8rem' }}><strong>KYC Documents:</strong> Identity documents (Aadhaar, PAN card) uploaded for verification purposes. These are stored securely and used only for identity verification.</p>
          <p style={{ marginTop: '0.8rem' }}><strong>Usage Data:</strong> Pages visited, actions taken on the Platform, session duration, and device/browser information.</p>
          <p style={{ marginTop: '0.8rem' }}><strong>Project and Interest Data:</strong> Information submitted in project listings and buyer interest forms, including organisation name, sector, and stated volume of interest.</p>
        </Section>

        <Section title="2. How We Use Your Information">
          <ul style={{ paddingLeft: '1.5rem', display: 'grid', gap: '0.5rem' }}>
            <li>To create and manage your account</li>
            <li>To verify your identity through our KYC process</li>
            <li>To provide Platform features including the simulated trading interface and project discovery directory</li>
            <li>To send account-related notifications (KYC status, new features)</li>
            <li>To improve the Platform based on usage patterns</li>
            <li>To comply with applicable laws and regulations</li>
          </ul>
          <p style={{ marginTop: '0.9rem' }}>We do not sell your personal information to third parties.</p>
        </Section>

        <Section title="3. KYC Document Handling">
          <p>Documents uploaded for KYC are:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.6rem', display: 'grid', gap: '0.4rem' }}>
            <li>Stored in encrypted form on secure servers</li>
            <li>Accessed only by authorised CarbonX staff for verification purposes</li>
            <li>Not shared with third parties except as required by law</li>
            <li>Retained for a minimum period required under applicable Indian regulations</li>
          </ul>
        </Section>

        <Section title="4. Cookies and Tracking">
          <p>We use session cookies to maintain your login state and local storage to store your preferences (such as theme settings). We may use analytics tools to understand aggregate usage patterns. We do not use third-party advertising cookies.</p>
        </Section>

        <Section title="5. Data Sharing">
          <p>We may share your information in the following limited circumstances:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.6rem', display: 'grid', gap: '0.4rem' }}>
            <li><strong>Service providers:</strong> Payment processors (e.g. Stripe) for wallet deposits — governed by their own privacy policies</li>
            <li><strong>Legal requirements:</strong> Where required by applicable Indian law or court order</li>
            <li><strong>Project interest matching:</strong> When you express interest in a project listing, a summary of your interest (name, organisation, volume, notes) may be shared with the project developer to facilitate offline follow-up</li>
          </ul>
        </Section>

        <Section title="6. Data Retention">
          <p>We retain your account information for as long as your account is active, plus a reasonable period thereafter as required by law. KYC documents are retained as required under applicable Know Your Customer regulations. You may request deletion of your account by contacting us — we will process such requests subject to any applicable legal retention requirements.</p>
        </Section>

        <Section title="7. Data Security">
          <p>We implement appropriate technical and organisational measures to protect your personal information, including:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.6rem', display: 'grid', gap: '0.4rem' }}>
            <li>HTTPS encryption for all data in transit</li>
            <li>Password hashing using industry-standard bcrypt</li>
            <li>JWT-based session management with token expiry</li>
            <li>Access controls limiting staff access to personal data</li>
          </ul>
          <p style={{ marginTop: '0.8rem' }}>No method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security but commit to notifying affected users in the event of a data breach.</p>
        </Section>

        <Section title="8. Your Rights">
          <p>Under applicable Indian data protection law, you have the right to:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.6rem', display: 'grid', gap: '0.4rem' }}>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your account and personal data (subject to legal retention requirements)</li>
            <li>Withdraw consent for specific data processing activities</li>
          </ul>
          <p style={{ marginTop: '0.8rem' }}>To exercise these rights, contact us at <span style={{ color: 'var(--neon-cyan)' }}>privacy@carbonx.in</span></p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>The Platform is not intended for users under 18 years of age. We do not knowingly collect personal information from minors.</p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We will notify registered users of material changes via email or Platform notification. Continued use of the Platform after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="11. Contact">
          <p>For privacy-related queries or to exercise your rights, contact us at:</p>
          <p style={{ marginTop: '0.6rem' }}>
            CarbonX Technology Private Limited<br />
            Email: <span style={{ color: 'var(--neon-cyan)' }}>privacy@carbonx.in</span>
          </p>
        </Section>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
        <Link to="/terms" className="btn btn-secondary">Terms of Service</Link>
        <Link to="/" className="btn btn-secondary">Back to Home</Link>
      </div>
    </div>
  );
}
